import React, { useEffect, useMemo, useState } from "react";
import client from "../utils/Client";// your axios instance
import { HiOutlineDownload, HiOutlineEye, HiOutlineX } from "react-icons/hi";
import { AiOutlineFile } from "react-icons/ai";

const isImage = (name = "") => /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(name);
const isPdf   = (name = "") => /\.pdf$/i.test(name);
const fileNameFromPath = (p = "") => p.split("/").pop() || "file";

const normalizeUrl = (filePath = "") => {
  // If the server already returns absolute URLs, use them.
  if (/^https?:\/\//i.test(filePath)) return filePath;
  // Otherwise assume it's a relative API path (e.g. "/uploads/..")
  // Let axios baseURL handle it for XHR (preview/download), but for <iframe>/<img>
  // we still prefer blob URLs (see below) to carry auth headers.
  return filePath.startsWith("/") ? filePath : `/${filePath}`;
};

export default function AttachmentModal({
  open,
  onClose,
  attachments = [],
  title = "Attachments",
}) {
  const [active, setActive] = useState(null);        // selected attachment
  const [previewUrl, setPreviewUrl] = useState(null); // blob/object URL for preview
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // pick first item when modal opens
  useEffect(() => {
    if (open && attachments?.length) {
      setActive(attachments[0]);
    } else {
      setActive(null);
      setPreviewUrl(null);
      setErr("");
    }
  }, [open, attachments]);

  // (Re)load preview when selection changes
  useEffect(() => {
    let revokeTimer;
    const load = async () => {
      setErr("");
      setPreviewUrl(null);
      if (!active) return;

      const name = active.fileName || fileNameFromPath(active.filePath);
      if (!isImage(name) && !isPdf(name)) {
        // non-previewable types -> no preview, just show a placeholder
        return;
      }

      setLoading(true);
      try {
        // Use XHR with auth headers (axios) and turn into a blob URL
        const url = normalizeUrl(active.filePath);
        const res = await client.get("attachments"+url, { responseType: "blob" });
        const objectUrl = URL.createObjectURL(res.data);
        setPreviewUrl(objectUrl);

        // Clean up URL later
        revokeTimer = setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      } catch (e) {
        setErr("Preview unavailable. You can still download the file.");
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (revokeTimer) clearTimeout(revokeTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.filePath]);

  const handleDownload = async (a) => {
    try {
      const name = a.fileName || fileNameFromPath(a.filePath);
      const url  = normalizeUrl(a.filePath);
      const res  = await client.get("attachments"+url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      alert("Could not download file.");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="bg-white w-[95vw] max-w-6xl max-h-[90vh] rounded-lg shadow-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-lg">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-2 hover:bg-gray-100"
            aria-label="Close"
          >
            <HiOutlineX size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col md:flex-row gap-4 p-4 overflow-hidden">
          {/* Left: Preview area */}
          <div className="flex-1 min-h-[40vh] border rounded-md overflow-hidden flex items-center justify-center">
            {!active ? (
              <div className="p-6 text-gray-500">No attachment selected</div>
            ) : loading ? (
              <div className="p-6 text-gray-500">Loading preview…</div>
            ) : err ? (
              <div className="p-6 text-gray-500">{err}</div>
            ) : previewUrl && isImage(active.fileName || active.filePath) ? (
              <img
                src={previewUrl}
                alt={active.fileName || "attachment"}
                className="max-w-full max-h-[70vh] object-contain"
              />
            ) : previewUrl && isPdf(active.fileName || active.filePath) ? (
              // Show PDF in an <iframe> using the blob URL
              <iframe
                title={active.fileName || "PDF"}
                src={previewUrl}
                className="w-full h-[70vh]"
              />
            ) : (
              <div className="p-6 text-gray-500 flex flex-col items-center">
                <AiOutlineFile size={48} />
                <div className="mt-2">No inline preview for this file type</div>
              </div>
            )}
          </div>

          {/* Right: List */}
          <div className="md:w-[320px] w-full border rounded-md overflow-auto">
            <ul className="divide-y">
              {attachments.map((a) => {
                const name = a.fileName || fileNameFromPath(a.filePath);
                const selected = a === active;
                return (
                  <li
                    key={a.attachmentId || a.filePath}
                    className={`p-3 flex items-start gap-3 cursor-pointer ${
                      selected ? "bg-gray-50" : "hover:bg-gray-50"
                    }`}
                    onClick={() => setActive(a)}
                  >
                    <AiOutlineFile size={22} className="mt-1 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{name}</div>
                      {a.officer?.name && (
                        <div className="text-xs text-gray-500 truncate">
                          by {a.officer.name}
                        </div>
                      )}
                      {a.datetimeInserted && (
                        <div className="text-xs text-gray-500 truncate">
                          {new Date(a.datetimeInserted).toLocaleString()}
                        </div>
                      )}
                      <div className="mt-2 flex gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActive(a);
                          }}
                          className="text-sm inline-flex items-center gap-1 px-2 py-1 rounded border hover:bg-gray-100"
                        >
                          <HiOutlineEye /> Preview
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(a);
                          }}
                          className="text-sm inline-flex items-center gap-1 px-2 py-1 rounded border hover:bg-gray-100"
                        >
                          <HiOutlineDownload /> Download
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}

              {attachments.length === 0 && (
                <li className="p-3 text-sm text-gray-500">No attachments.</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
